// Utility functions for map visualizations

export interface SVGCoordinate {
  x: number;
  y: number;
  index: number;
}

/**
 * Convert lat/lng coordinates to SVG coordinates
 */
export const convertToSVGCoordinates = (
  latitudes: number[],
  longitudes: number[],
  width: number,
  height: number,
  padding: number = 20,
  bottomPadding?: number
): SVGCoordinate[] => {
  if (latitudes.length === 0 || longitudes.length === 0) return [];

  const actualBottomPadding = bottomPadding ?? padding;

  const minLat = Math.min(...latitudes);
  const maxLat = Math.max(...latitudes);
  const minLng = Math.min(...longitudes);
  const maxLng = Math.max(...longitudes);

  const latRange = maxLat - minLat || 1;
  const lngRange = maxLng - minLng || 1;

  // Calculate scale to fit within SVG bounds with asymmetric padding
  const scaleX = (width - 2 * padding) / lngRange;
  const scaleY = (height - padding - actualBottomPadding) / latRange;

  return latitudes.map((lat, i) => ({
    x: padding + (longitudes[i] - minLng) * scaleX,
    y: height - actualBottomPadding - (lat - minLat) * scaleY, // Flip Y axis for SVG
    index: i,
  }));
};

/**
 * Color interpolation based on time lost
 */
export const getColorForTimeLost = (timeLost: number, maxTimeLost: number): string => {
  if (maxTimeLost === 0) return '#22c55e'; // Green if no time lost

  const intensity = Math.min(timeLost / maxTimeLost, 1);

  // Color gradient: Green (0) -> Yellow (0.5) -> Red (1)
  if (intensity <= 0.5) {
    const ratio = intensity * 2;
    const r = Math.round(34 + (255 - 34) * ratio);
    const g = Math.round(197 + (255 - 197) * ratio);
    const b = 94;
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    const ratio = (intensity - 0.5) * 2;
    const r = 255;
    const g = Math.round(255 - 255 * ratio);
    const b = Math.round(94 - 94 * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

/**
 * Color interpolation based on lap time (fastest = green, slowest = red)
 */
export const getColorForLapTime = (lapTime: number, fastestTime: number, slowestTime: number): string => {
  if (fastestTime === slowestTime) return '#22c55e'; // Green if all times are the same

  const intensity = (lapTime - fastestTime) / (slowestTime - fastestTime);

  // Color gradient: Green (fastest) -> Yellow (medium) -> Red (slowest)
  if (intensity <= 0.5) {
    const ratio = intensity * 2;
    const r = Math.round(34 + (255 - 34) * ratio);
    const g = Math.round(197 + (255 - 197) * ratio);
    const b = 94;
    return `rgb(${r}, ${g}, ${b})`;
  } else {
    const ratio = (intensity - 0.5) * 2;
    const r = 255;
    const g = Math.round(255 - 255 * ratio);
    const b = Math.round(94 - 94 * ratio);
    return `rgb(${r}, ${g}, ${b})`;
  }
};

/**
 * Find coordinates for a given track position in meters using displacement array
 */
export const getCoordinatesAtPosition = (
  positionMeters: number, 
  coordinates: SVGCoordinate[], 
  displacement: number[]
): SVGCoordinate | null => {
  if (coordinates.length === 0 || !displacement || displacement.length === 0) return null;

  const totalDistance = displacement[displacement.length - 1];
  
  // Handle positions beyond track length
  if (positionMeters >= totalDistance) {
    return coordinates[coordinates.length - 1];
  }
  
  if (positionMeters <= 0) {
    return coordinates[0];
  }

  // Find the closest index for the given position in meters
  let closestIndex = 0;
  let minDiff = Math.abs(displacement[0] - positionMeters);
  
  for (let i = 1; i < displacement.length; i++) {
    const diff = Math.abs(displacement[i] - positionMeters);
    if (diff < minDiff) {
      minDiff = diff;
      closestIndex = i;
    }
  }

  return coordinates[closestIndex] || coordinates[0];
};

/**
 * Get path segment indices between two positions using displacement array
 */
export const getPathSegmentIndices = (
  startPos: number, 
  endPos: number, 
  displacement: number[]
): { startIndex: number; endIndex: number } => {
  if (!displacement || displacement.length === 0) return { startIndex: 0, endIndex: 0 };

  // Convert positions to indices
  let startIndex = 0;
  let endIndex = displacement.length - 1;
  
  // Find start index
  for (let i = 0; i < displacement.length; i++) {
    if (displacement[i] >= startPos) {
      startIndex = i;
      break;
    }
  }
  
  // Find end index
  for (let i = startIndex; i < displacement.length; i++) {
    if (displacement[i] >= endPos) {
      endIndex = i;
      break;
    }
  }
  
  return { startIndex, endIndex };
};
